import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { GeneratedGenieDocument } from "@/types/genie-documents/genie-document";

interface GenieDocumentEditorProps {
  doc: GeneratedGenieDocument;
  onChange: (doc: GeneratedGenieDocument) => void;
}

const FIELD =
  "w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-genie-primary";

/**
 * Plain, in-place editing of a generated document.
 *
 * Deliberately not a rich text editor: the document is a fixed structure
 * (title, subtitle, ordered heading/body sections) and the branded renderer owns
 * every visual decision. What the owner needs is to fix wording and drop the
 * section that got it wrong.
 */
export const GenieDocumentEditor = ({ doc, onChange }: GenieDocumentEditorProps) => {
  const updateSection = (index: number, key: "heading" | "body", value: string) =>
    onChange({
      ...doc,
      sections: doc.sections.map((section, i) =>
        i === index ? { ...section, [key]: value } : section,
      ),
    });

  const removeSection = (index: number) =>
    onChange({ ...doc, sections: doc.sections.filter((_, i) => i !== index) });

  const addSection = () =>
    onChange({ ...doc, sections: [...doc.sections, { heading: "", body: "" }] });

  return (
    <div className="space-y-5 rounded-2xl border border-gray-200 bg-white p-5">
      <div>
        <label className="mb-1 block text-xs font-semibold text-gray-500">Title</label>
        <input
          value={doc.title}
          onChange={(event) => onChange({ ...doc, title: event.target.value })}
          className={`${FIELD} text-base font-semibold`}
        />
      </div>

      <div>
        <label className="mb-1 block text-xs font-semibold text-gray-500">Subtitle</label>
        <input
          value={doc.subtitle || ""}
          onChange={(event) => onChange({ ...doc, subtitle: event.target.value })}
          placeholder="One line under the title — also the summary on your Genie Page"
          className={FIELD}
        />
      </div>

      {doc.sections.map((section, index) => (
        <div key={index} className="space-y-2 border-t border-gray-100 pt-4">
          <div className="flex items-center gap-2">
            <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md bg-gray-100 text-[11px] font-bold text-gray-500">
              {index + 1}
            </span>
            <input
              value={section.heading}
              onChange={(event) => updateSection(index, "heading", event.target.value)}
              placeholder="Section heading"
              className={`${FIELD} font-semibold`}
            />
            <Button
              variant="ghost"
              size="icon"
              onClick={() => removeSection(index)}
              aria-label={`Remove section ${index + 1}`}
              className="h-9 w-9 flex-shrink-0 text-gray-400 hover:bg-red-50 hover:text-red-600"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
          <textarea
            value={section.body}
            onChange={(event) => updateSection(index, "body", event.target.value)}
            rows={5}
            className={`${FIELD} resize-y`}
          />
        </div>
      ))}

      <Button variant="outline" onClick={addSection} className="gap-1.5">
        <Plus className="h-4 w-4" />
        Add a section
      </Button>
    </div>
  );
};

export default GenieDocumentEditor;
